import React from "react";
import { Space, Descriptions, Table, Tooltip, Tag, Switch } from "antd";
import StepHeader from "../../Evaluation/StepHeader";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { RequestViewButton } from "../../shared/button/Buttons";

interface TestSetData {
  name?: string;
  description?: string;
  requests?: ChatCompletionRequest[];
  is_golden?: boolean;
}

interface TestSetReviewProps {
  testSetData: TestSetData;
  apiKey: string;
  onChange?: (data: Partial<TestSetData>) => void;
}

const TestSetReview: React.FC<TestSetReviewProps> = ({
  testSetData,
  apiKey,
  onChange = () => {},
}) => {
  const columns = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      ellipsis: true,
    },
    {
      title: "Tags",
      key: "tags",
      width: "30%",
      render: (_: any, record: any) => {
        let tags: string[] = [];

        if (record.metadata?._maitai_test_request_meta) {
          try {
            const meta = JSON.parse(record.metadata._maitai_test_request_meta);
            tags = meta.tags || [];
          } catch (e) {
            console.error("Error parsing metadata:", e);
          }
        }

        return tags.length > 0 ? (
          <Space size={[0, 4]} wrap>
            {tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#999" }}>No tags</span>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <RequestViewButton
          buttonStyle={{ color: "#21b892" }}
          apiKey={apiKey}
          request={record}
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}>
          View
        </RequestViewButton>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <StepHeader
        title="Review Test Set"
        description="Review your test set configuration before creating"
      />

      <Descriptions
        bordered
        column={1}
        size="middle"
        labelStyle={{ width: "200px", background: "#fafafa" }}>
        <Descriptions.Item label="Name">
          {testSetData.name || "Not set"}
        </Descriptions.Item>

        <Descriptions.Item label="Description">
          {testSetData.description || "No description provided"}
        </Descriptions.Item>

        <Descriptions.Item label="Number of Requests">
          {testSetData.requests?.length || 0} requests
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              Golden
              <Tooltip title="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
                <InfoCircleOutlined
                  style={{
                    color: "rgba(0, 0, 0, 0.45)",
                    fontSize: "14px",
                  }}
                />
              </Tooltip>
            </Space>
          }>
          <Switch
            checked={testSetData.is_golden}
            onChange={(checked) => onChange({ is_golden: checked })}
          />
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: "24px" }}>
        <h3>Requests</h3>
        <Table
          dataSource={testSetData.requests}
          columns={columns}
          rowKey="request_id"
          pagination={false}
          size="middle"
        />
      </div>
    </Space>
  );
};

export default TestSetReview;
